import {action, computed, makeAutoObservable} from "mobx";

export interface PriceEntry {
  blockNumber: number;
  price: number;
  time: string | number;
}

export class PriceStore {
  private data = new Array<PriceEntry>();
  private minPrice = 0;
  private maxPrice = 0;
  private currentPrice = 0;

  constructor() {
    makeAutoObservable(this);
  }

  @action
  clearPrice() {
    this.data.length  = 0;
    this.minPrice     = 0;
    this.maxPrice     = 0;
    this.currentPrice = 0;
  }

  @action
  addPricePoints(points: PriceEntry[]) {
    // update the state
    this.data = points;
    this.maxPrice = points[0].price;
    this.minPrice = points[points.length-1].price;
  }

  @computed
  setCurrentPrice(price: number) {
    this.currentPrice = price;
  }

  @computed
  getPrices() {
    return [...this.data];
  }

  @computed
  isEmpty() {
    return this.data.length === 0;
  }

  @computed
  getCurrentPrice() {
    return this.currentPrice;
  }

  @computed
  asNumberSeconds(time: string | number): number {
    return (typeof time === "string" ? Number.parseInt(time) : time) * 1000;
  }

  @computed
  getFormatedPrices(inPast: boolean) {

    // Select the format based on the time range
    let hoursGranularity = true;

    // If time distance between end points is greater than a day
    if (this.data.length > 1) {
      let date1 = new Date(this.asNumberSeconds(this.data[this.data.length-1].time));
      let date2 = new Date(this.asNumberSeconds(this.data[0].time));

      // 2 days in seconds
      if (Math.abs(date2.getTime() - date1.getTime())  > (172800 * 1000)) {
        hoursGranularity = false;
      }
    }

    let now = new Date(Date.now());

    let toReturn = [
      ...this.data.filter((e) => {
        // filter out all the data points that are before/after now in time
        let date = new Date(this.asNumberSeconds(e.time));

        if (inPast && date <= now){
          return true;
        }

        if (!inPast && date >= now){
          return true;
        }

        return false;
      }).map((e) => {
        let asSeconds = this.asNumberSeconds(e.time);
        let date = new Date(asSeconds);

        let month = date.getMonth() + 1;
        let day = date.getDate();
        let hours = date.getHours();
        let minutes = "0" + date.getMinutes();
        let formattedTime = "";

        // Format time times according to how long the auction runs for
        if (hoursGranularity) {
          formattedTime = hours + ':' + minutes.substr(-2);
        } else {
          formattedTime = day + '/' + month;
        }

        return {
          time: `${formattedTime}`,
          price: e.price,
          fullDate: date.toLocaleDateString(),
        };
      }),
    ];

    return toReturn;
  }

  @computed
  getMinBidPrice() {
    return this.minPrice;
  }

  @computed
  getBidStepPrice() {
    return 1;
  }

  @computed
  getPriceRange() {
    let delta = (this.maxPrice - this.minPrice) * 0.2;

    return [
      delta < this.minPrice ? Math.round(this.minPrice - delta) : 0,
      Math.round(this.maxPrice + delta),
    ];
  }
}
